import React, { useState } from 'react';
import { Trash2, AlertTriangle, X, Loader2 } from 'lucide-react';
import { getAuth, deleteUser } from 'firebase/auth';

const AccountDeletionModal = ({ isOpen, onClose, onDeleteComplete }) => {
    const [confirmText, setConfirmText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const auth = getAuth();

    // Backend API URL - UPDATE THIS with your Cloud Run URL
    const API_URL = process.env.REACT_APP_API_URL;

    const handleDelete = async () => {
        if (confirmText.toUpperCase() !== 'DELETE') {
            setError('Please type DELETE to confirm');
            return;
        }

        setIsDeleting(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user) {
                throw new Error('No user logged in');
            }

            // Call backend to delete user data from database
            try {
                const token = await user.getIdToken(true);
                const response = await fetch(`${API_URL}/api/account/delete`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                
                if (!response.ok) {
                    throw new Error('Failed to delete backend data');
                }
            } catch (backendError) {
                console.error('Failed to delete backend data:', backendError);
                setError('Failed to delete some data. Please contact support.');
                setIsDeleting(false);
                return;
            }

            // Delete Firebase user account
            await deleteUser(user);
            
            // Call completion callback
            if (onDeleteComplete) {
                onDeleteComplete();
            }
        } catch (err) {
            console.error('Account deletion error:', err);
            
            if (err.code === 'auth/requires-recent-login') {
                setError('For security, please sign out and sign in again before deleting your account.');
            } else {
                setError(`Failed to delete account: ${err.message}`);
            }
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setConfirmText('');
            setError('');
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <style>{`
                @keyframes modal-fade-in {
                    0% { opacity: 0; }
                    100% { opacity: 1; }
                }
                @keyframes modal-slide-up {
                    0% { transform: translateY(20px); opacity: 0; }
                    100% { transform: translateY(0); opacity: 1; }
                }
                .modal-backdrop {
                    animation: modal-fade-in 0.3s ease-out;
                }
                .modal-content {
                    animation: modal-slide-up 0.4s ease-out;
                }
            `}</style>
            
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 modal-backdrop">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md modal-content">
                    {/* Header */}
                    <div className="bg-red-600 text-white p-6 rounded-t-3xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-2 rounded-full">
                                    <AlertTriangle className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">Delete Account</h2>
                                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                                </div>
                            </div>
                            {!isDeleting && (
                                <button 
                                    onClick={handleClose}
                                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                            <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5" />
                                Warning: Permanent Deletion
                            </h3>
                            <p className="text-red-800 text-sm mb-2">
                                Deleting your account will permanently remove:
                            </p>
                            <ul className="text-red-800 text-sm space-y-1 ml-4 list-disc">
                                <li>Your account and login access</li>
                                <li>All your quiz progress and scores</li>
                                <li>Your study history and statistics</li>
                                <li>All personal data associated with your account</li>
                            </ul>
                            <p className="text-red-900 font-semibold text-sm mt-3">
                                This action cannot be reversed.
                            </p>
                        </div>

                        {error && (
                            <div className="bg-red-100 border-2 border-red-300 rounded-xl p-4 mb-4">
                                <p className="text-red-800 text-sm font-semibold">{error}</p>
                            </div>
                        )}

                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Type <span className="text-red-600">DELETE</span> to confirm:
                            </label>
                            <input
                                type="text"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type DELETE here"
                                disabled={isDeleting}
                                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:border-red-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                You must type DELETE in capital letters to proceed
                            </p>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleClose}
                                disabled={isDeleting}
                                className="flex-1 px-6 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting || confirmText.toUpperCase() !== 'DELETE'}
                                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                                    isDeleting || confirmText.toUpperCase() !== 'DELETE'
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg'
                                }`}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-5 h-5" />
                                        Delete Account
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AccountDeletionModal;
